import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { Badge } from "../../../../components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead,
    TableHeader, TableRow,
} from "../../../../components/ui/table";
import { useToast } from "../../../../hooks/use-toast";
import { grantFreeResponses, getFreeResponseHistory } from '../../../../services/AdminApis';

const formatDate = (value) => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
};

const SOURCE_LABELS = {
    ADMIN_GRANT: 'Admin Grant',
    COUPON_CLAIM: 'Coupon Claim',
    INITIAL_SIGNUP_BONUS: 'Signup Bonus',
    PROSPECT_GRANT: 'Prospect Bonus',
};

const GrantFreeResponses = ({ sellerProfileId, onUpdateSuccess }) => {
    const [responses, setResponses] = useState('');
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const { toast } = useToast();

    const fetchHistory = useCallback(async () => {
        if (!sellerProfileId) return;
        setHistoryLoading(true);
        try {
            const res = await getFreeResponseHistory(sellerProfileId);
            setHistory(res.data.items || []);
        } catch (err) {
            console.error('Failed to fetch free response history:', err);
        } finally {
            setHistoryLoading(false);
        }
    }, [sellerProfileId]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory, refreshKey]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const responsesToGrant = parseInt(responses, 10);

        if (!responsesToGrant || responsesToGrant <= 0) {
            toast({
                variant: "destructive",
                title: "Invalid Input",
                description: "Please enter a valid number of responses to grant.",
            });
            return;
        }
        if (!reason.trim()) {
            toast({
                variant: "destructive",
                title: "Reason Required",
                description: "Please provide a reason for granting free responses.",
            });
            return;
        }

        setIsLoading(true);
        try {
            const response = await grantFreeResponses(sellerProfileId, {
                responses_to_grant: responsesToGrant,
                reason: reason.trim(),
            });

            toast({
                title: "Success",
                description: `Successfully granted ${responsesToGrant} free responses.`,
            });

            if(onUpdateSuccess) {
                onUpdateSuccess(response.data);
            }

            setResponses('');
            setReason('');
            setRefreshKey(k => k + 1);

        } catch (error) {
            const errorMessage = error.response?.data?.detail || "An unexpected error occurred.";
            toast({
                variant: "destructive",
                title: "Granting Failed",
                description: errorMessage,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="p-4 border rounded-lg">
                <h4 className="text-md font-semibold mb-2">Grant Free Responses</h4>
                <p className="text-sm text-muted-foreground mb-4">
                    Add a number of free lead responses to the seller's account. Your ability to grant responses is limited by your user group.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="responses-to-grant">Number of Responses</Label>
                        <Input
                            id="responses-to-grant"
                            type="number"
                            value={responses}
                            onChange={(e) => setResponses(e.target.value)}
                            placeholder="e.g., 5"
                            min="1"
                            disabled={isLoading}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="grant-reason">Reason</Label>
                        <Textarea
                            id="grant-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g., Customer service resolution for ticket #12345"
                            disabled={isLoading}
                        />
                    </div>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? 'Granting...' : 'Grant Responses'}
                    </Button>
                </form>
            </div>

            <div className="p-4 border rounded-lg">
                <h4 className="text-md font-semibold mb-4">Grant History</h4>

                {historyLoading ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Loading history...</p>
                ) : history.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No grant history found.</p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Granted By</TableHead>
                                <TableHead>Count</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead>Balance After</TableHead>
                                <TableHead>Reason</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {history.map((entry, idx) => (
                                <TableRow key={idx}>
                                    <TableCell className="whitespace-nowrap">{formatDate(entry.created_at)}</TableCell>
                                    <TableCell>{entry.admin_email || '—'}</TableCell>
                                    <TableCell>
                                        <span className="text-green-600 font-medium">+{entry.responses_transacted}</span>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">
                                            {SOURCE_LABELS[entry.entry_source] || entry.entry_source}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{entry.balance_after}</TableCell>
                                    <TableCell className="max-w-xs truncate" title={entry.notes || ''}>
                                        {entry.notes || '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
};

export default GrantFreeResponses;
